import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import apiClient from "@/api-client";
import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";

export interface PersonaService {
	type: string;
	plugin: { id: string; title?: string };
	settings?: Record<string, any>;
}

export interface Persona {
	id: string;
	name: string;
	description: string | null;
	prompt: string;
	services: PersonaService[];
	settings: Record<string, any>;
	/** The agent this caller was built for. Null means it suits any of them. */
	agentId: string | null;
	agentName: string | null;
}

export const personasKeys = {
	all: ["personas"] as const,
	lists: () => [...personasKeys.all, "list"] as const,
	list: (query?: string) => [...personasKeys.lists(), { query }] as const,
	details: () => [...personasKeys.all, "detail"] as const,
	detail: (id: string) => [...personasKeys.details(), id] as const,
	serviceTypes: () => [...personasKeys.all, "service-types"] as const,
};

/**
 * Which services a persona can be configured with. Read from the server rather than
 * hard-coded, so the tabs on the configuration form cannot drift from what it will accept.
 */
export function usePersonaServiceTypes() {
	return useQuery<string[]>({
		queryKey: personasKeys.serviceTypes(),
		queryFn: async () => {
			const response = await apiClient.get("/personas/service-types");
			return response.data;
		},
		staleTime: Infinity,
	});
}

export function usePersona(id?: string) {
	return useQuery<Persona>({
		queryKey: personasKeys.detail(id ?? ""),
		queryFn: async () => {
			const response = await apiClient.get(`/personas/${id}`);
			return response.data;
		},
		enabled: Boolean(id),
	});
}

export function useDeletePersona() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			const response = await apiClient.delete(`/personas/${id}`);
			return response.data;
		},
		onSuccess: (_, id) => {
			queryClient.removeQueries({ queryKey: personasKeys.detail(id) });
			queryClient.invalidateQueries({ queryKey: personasKeys.lists() });
			toast.success("Persona deleted");
		},
		onError: (error: any) => {
			logger.error(error);
			const message = error?.response?.data?.message;
			toast.error(Array.isArray(message) ? message[0] : message || "Could not delete the persona");
		},
	});
}

/** Flat list, for pickers rather than tables. */
export function usePersonasList() {
	return useQuery<Persona[]>({
		queryKey: [...personasKeys.lists(), "all"],
		queryFn: async () => {
			const response = await apiClient.get("/personas?limit=100&offset=0");
			return response.data.data;
		},
	});
}
